import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../environment';
import {
  PublicBookingConfirmation,
  PublicBookingRequest,
  PublicDeparturesResponse,
  PublicOperator,
  PublicSharedTrip,
  PublicTripShare,
  PublicTrip,
  PublicTripSearchFilters
} from '../models/public-booking.model';
import { normalizeApiArray } from '../utils/api-response.util';

@Injectable({
  providedIn: 'root'
})
export class PublicBookingService {
  private apiUrl = `${environment.apiUrl}/public`;

  constructor(private http: HttpClient) {}

  getOperators(filters: PublicTripSearchFilters = {}): Observable<PublicOperator[]> {
    return this.http.get<unknown>(`${this.apiUrl}/operators`, { params: this.toParams(filters) }).pipe(
      map(response => normalizeApiArray<PublicOperator>(response))
    );
  }

  getTrips(filters: PublicTripSearchFilters = {}): Observable<PublicTrip[]> {
    return this.http.get<unknown>(`${this.apiUrl}/trips`, { params: this.toParams(filters) }).pipe(
      map(response => normalizeApiArray<PublicTrip>(response))
    );
  }

  getDepartures(filters: { operatorId?: number | null; limit?: number } = {}): Observable<PublicDeparturesResponse> {
    return this.http.get<PublicDeparturesResponse>(`${this.apiUrl}/departures`, {
      params: this.toParams(filters)
    });
  }

  getTrip(tripId: number): Observable<PublicTrip> {
    return this.http.get<PublicTrip>(`${this.apiUrl}/trips/${tripId}`);
  }

  shareTrip(tripId: number): Observable<PublicTripShare> {
    return this.http.post<PublicTripShare>(`${this.apiUrl}/trips/${tripId}/share`, null);
  }

  getSharedTrip(publicToken: string): Observable<PublicSharedTrip> {
    return this.http.get<PublicSharedTrip>(
      `${this.apiUrl}/shared-trips/${encodeURIComponent(publicToken)}`
    );
  }

  bookTrip(tripId: number, payload: PublicBookingRequest): Observable<PublicBookingConfirmation> {
    return this.http.post<PublicBookingConfirmation>(`${this.apiUrl}/trips/${tripId}/book`, payload);
  }

  getBooking(bookingReference: string): Observable<PublicBookingConfirmation> {
    return this.http.get<PublicBookingConfirmation>(`${this.apiUrl}/bookings/${encodeURIComponent(bookingReference)}`);
  }

  getTicket(token: string): Observable<unknown> {
    return this.http.get<unknown>(`${this.apiUrl}/tickets/${encodeURIComponent(token)}`);
  }

  private toParams(filters: object): HttpParams {
    let params = new HttpParams();

    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && String(value).trim()) {
        params = params.set(key, String(value).trim());
      }
    });

    return params;
  }
}
